// layout/Navigation/DemoToggle.tsx
// One-click Demo toggle in the TopNav for non-superuser users.
// Eligible: broadcaster / adtech / agency / distributor. Hidden for customers and superusers
// (superusers already have the full ChameleonPicker; customers are excluded by decision).
// Demo chameleons the user into the ACME demo agency; toggling off returns them to their own entity.
import { Button, Tooltip } from '@mui/material';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import { useAuth } from '@/contexts';
import { useChameleon } from '@/contexts/ChameleonContext';
import { alpha, colors } from '@/styles/base/tokens';

// ACME demo agency — the self-resetting sandbox (see backend project_acme_demo_environment).
// Id 118 is stable across all environments (confirmed 2026-07-07), so a constant is safe here.
const DEMO_AGENCY_ID = 118;
const DEMO_AGENCY_NAME = 'ACME';

export function DemoToggle() {
    const { authState } = useAuth();
    const { chameleon, activate, deactivate } = useChameleon();

    const user = authState.user;
    const eligible =
        !!user && !user.isSuperUser && (user.isBroadcaster || user.isAdTechUser || user.isAgency || user.isDistributor);

    if (!eligible) return null;

    const inDemo = chameleon.active && chameleon.entityType === 'agency' && chameleon.entityId === DEMO_AGENCY_ID;

    const handleClick = () => {
        if (inDemo) {
            deactivate();
        } else {
            activate('agency', DEMO_AGENCY_ID, DEMO_AGENCY_NAME);
        }
    };

    return (
        <Tooltip
            title={
                inDemo
                    ? 'Exit demo — return to your own data'
                    : 'Demo mode — explore the platform with sample ACME data'
            }
            placement="bottom"
            arrow
        >
            <Button
                size="small"
                variant={inDemo ? 'contained' : 'outlined'}
                data-testid="demo-toggle-btn"
                onClick={handleClick}
                startIcon={<SlideshowOutlinedIcon sx={{ fontSize: '16px !important' }} />}
                sx={{
                    mr: 2,
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    py: '3px',
                    px: 1.2,
                    whiteSpace: 'nowrap',
                    ...(inDemo
                        ? {
                              bgcolor: 'warning.dark',
                              color: 'white',
                              '&:hover': { bgcolor: 'warning.main' },
                          }
                        : {
                              color: alpha(colors.appBarText, 0.85),
                              borderColor: alpha(colors.appBarText, 0.3),
                              '&:hover': { borderColor: alpha(colors.appBarText, 0.7), color: colors.appBarText },
                          }),
                }}
            >
                {inDemo ? 'Exit Demo' : 'Demo'}
            </Button>
        </Tooltip>
    );
}
